import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { GenericCoordinates } from '../../location.types'
import { delayedRetry } from '../../../../shared/utils/delayedRetryOperator'
import { DrrHttpClient } from '../../../../http/drr-http-client'
import { NEARBY_LANDMARK_RADIUS_METERS } from '../../location.constants'
import type {
  NearbyLandmark,
  NearbyLandmarkStrength,
  ReferenceSignal,
} from '../../map-landing.types'
import { WEAK_GEOCODE_PLACE_TYPES } from '../../map-reference.utils'

export interface AwsSearchNearbyResult {
  Title?: string
  PlaceType?: string
  Distance?: number
  Position?: [number, number]
  PlaceId?: string
  Categories?: Array<{ Id?: string; Name?: string; Primary?: boolean }>
  Address?: { Label?: string }
}

export interface AwsSearchNearbyResponse {
  ResultItems: AwsSearchNearbyResult[]
}

/**
 * How strongly one SearchNearby result orients a stranded member, or `null` to drop it. A business,
 * park or school is a landmark; a street or a street address is a real-but-weak reference (the map
 * already labels roads); anything else — a locality, a postal code — tells them nothing.
 */
const classifyNearbyPlaceType = (placeType?: string): NearbyLandmarkStrength | null => {
  if (placeType === 'PointOfInterest') {
    return 'poi'
  }
  if (placeType && WEAK_GEOCODE_PLACE_TYPES.has(placeType)) {
    return 'weak'
  }
  return null
}

/**
 * DRRWEB-3399 `nearby` map-reference detector, stage one — POSTs to the SearchNearby endpoint and
 * turns the result list into *candidates*. It deliberately does not decide whether the member has
 * a reference: a result 117 m away is "near" by radius yet invisible at z18, so that call belongs
 * to the map component, which knows the viewport (see resolveNearbyAgainstView).
 *
 * Errors are left to propagate rather than swallowed here, so the caller (the fetch effect in
 * location.effects.ts) treats a failure as "no opinion" by dispatching nothing — never as a
 * measured absence.
 */
@Injectable({ providedIn: 'root' })
export class AwsNearbyService {
  retryDelay = 2000
  /**
   * No retries: the detector tolerates total failure by design (the landing decides without it after
   * NEARBY_DECISION_TIMEOUT_MS), and while the route is not deployed every attempt is a permanent
   * 403 — retrying it only spends background requests no member can ever see the result of.
   */
  retries = 0

  constructor(private http: DrrHttpClient) {}

  searchNearby(coordinates: GenericCoordinates): Observable<ReferenceSignal> {
    const requestBody = {
      latitude: parseFloat(coordinates.latitude.toString()),
      longitude: parseFloat(coordinates.longitude.toString()),
      radius: NEARBY_LANDMARK_RADIUS_METERS,
    }

    // Errors (incl. the 403 "Missing Authentication Token" API Gateway returns while the route is
    // not deployed on an env) are left to propagate — the caller decides how to treat a failure,
    // per the fail-silent convention this detector follows (resolveIpRegion in location.effects.ts).
    return this.http
      .post<AwsSearchNearbyResponse>('bff/roadservice/geo/search-nearby', requestBody)
      .pipe(
        delayedRetry(this.retryDelay, this.retries),
        map((response) => this.toReferenceSignal(response))
      )
  }

  /**
   * An empty (or entirely unusable) result list is a *measured* absence — nothing named within the
   * radius — so it reports 'none' and the banded widen applies pre-paint. Any kept candidate makes
   * the signal view-dependent: `reference` is provisional ("something within radius", the strongest
   * kind found) and `landmarks` carries what the map component needs to check the view.
   */
  private toReferenceSignal(response: AwsSearchNearbyResponse): ReferenceSignal {
    const landmarks = this.toLandmarks(response.ResultItems ?? [])

    if (!landmarks.length) {
      return { reference: 'none', source: 'nearby' }
    }

    return {
      reference: landmarks.some((landmark) => landmark.strength === 'poi') ? 'poi' : 'weak',
      source: 'nearby',
      landmark: landmarks[0],
      landmarks,
    }
  }

  private toLandmarks(items: AwsSearchNearbyResult[]): NearbyLandmark[] {
    return items
      .reduce<NearbyLandmark[]>((kept, item) => {
        const strength = classifyNearbyPlaceType(item.PlaceType)
        const [lng, lat] = item.Position ?? []
        if (
          strength &&
          typeof lng === 'number' &&
          typeof lat === 'number' &&
          typeof item.Distance === 'number'
        ) {
          kept.push({
            title: item.Title || item.Address?.Label || '',
            position: [lng, lat],
            distanceMeters: item.Distance,
            strength,
          })
        }
        return kept
      }, [])
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
  }
}
